import React from 'react';
import { useGetProgressionsQuery } from '../../../../API/order/signingAppointment/SigningAppointmentAPI';
import { IStepperSubLabel, StepperTW } from '../../../../tailwindComponents/stepper/StepperTW';
import {
  eSigSyncProgressionLine,
  getLatestActiveLineIndex,
  getSigSyncProgressionLineLabel,
  ISigSyncProgressionLine,
  sortSigSyncProgressionLines,
} from '../../../../types/signingAppointment/ISigSyncProgressionLine';
import {
  getSigSyncEventTypeLabel,
  ISigSyncEventType,
  subLabelColorByEventType,
} from '../../../../types/signingAppointment/ISigSyncEventType';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../../types/shared/UUID';

export const AppointmentProgressions: React.FC = () => {
  const { orderId, appointmentId } = useParams<{ orderId: UUID; appointmentId: UUID }>();
  const { data: progressions } = useGetProgressionsQuery({ id: appointmentId, orderId }, {});

  if (!progressions || Object.keys(progressions).length === 0) {
    return <></>;
  }

  const progressionLines = getLinesToShow(progressions);
  const progressionLineLabels = progressionLines.map((line) => getSigSyncProgressionLineLabel(line));
  const progressionStates: Array<ISigSyncEventType | null> = progressionLines.map((line) => progressions[line]);
  const progressionStateSubLabels: Array<IStepperSubLabel> = _(progressionStates)
    .map((p) => {
      return !!p
        ? {
            text: getSigSyncEventTypeLabel(p),
            customStyles: subLabelColorByEventType[p],
          }
        : {
            text: undefined,
          };
    })
    .value();

  const currentActiveLine = getLatestActiveLineIndex(progressionLines, progressions);
  return (
    <div>
      <StepperTW
        data-test-id={'signing-appointment-progressions'}
        current={currentActiveLine}
        labels={progressionLineLabels}
        subLabels={progressionStateSubLabels}
      />
    </div>
  );
};

const getLinesToShowForTerminalProgression = (
  progressions: Record<ISigSyncProgressionLine, ISigSyncEventType | null>,
  terminalProgressionName: eSigSyncProgressionLine
): ISigSyncProgressionLine[] => {
  const lines = (Object.keys(progressions) as ISigSyncProgressionLine[]).filter(
    (l) => l !== terminalProgressionName && l !== 'COMPLETE'
  );

  const sortedLines = sortSigSyncProgressionLines(lines);
  let latestActiveLineIndex = 0;

  for (let i = 0; i < sortedLines.length; i++) {
    const line = sortedLines[i];
    if (!!progressions[line]) {
      latestActiveLineIndex = i;
    }
  }

  const linesForCanceled = sortedLines.slice(0, latestActiveLineIndex + 1);
  linesForCanceled.push(terminalProgressionName);

  return linesForCanceled;
};

export const getLinesToShow = (
  progressions: Record<ISigSyncProgressionLine, ISigSyncEventType | null>
): ISigSyncProgressionLine[] => {
  if (!!progressions[eSigSyncProgressionLine.Canceled]) {
    return getLinesToShowForTerminalProgression(progressions, eSigSyncProgressionLine.Canceled);
  }
  if (!!progressions[eSigSyncProgressionLine.NotSigned]) {
    return getLinesToShowForTerminalProgression(progressions, eSigSyncProgressionLine.NotSigned);
  }
  const lines = (Object.keys(progressions) as ISigSyncProgressionLine[]).filter(
    (l) => l !== 'CANCELED' && l !== 'NOT_SIGNED'
  );

  return sortSigSyncProgressionLines(lines);
};
